"use client";
import React, { useState, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import { ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import EventLink from "@/components/Link/EventLink/EventLink";
import ProductSearch from "@/components/ProductSearch";
import SitewideBanner from "@/components/SitewideBanner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import getUserInfo from "@/lib/auth/getUserInfo";
import { Nav } from "@/types/Navigation";
import UserMenu from "../UserMenu";
import { ActiveBanner } from "../types";
import useLmsUserStatus from "@/lib/lms/getLmsUserStatus";
import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";
import { useAuth } from "@/components/AuthProvider";
import { useAuthxNavUrls } from "@/hooks/useAuthxNavUrls";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import useProductSearch from "@/monarch/rules/productSearch/useProductSearch";
import HeaderLanguageToggle from "../HeaderLanguageToggle";
import { useNavigationMessages } from "@/i18n/hooks/useNavigationMessages";

type Props = {
  navigation: Nav;
  openNav: boolean;
  setOpenNav: Function;
  activeBanner?: ActiveBanner;
  course?: ProcessedCourse;
  program?: Program;
  navButtonRef: React.RefObject<HTMLButtonElement | null>;
  bannerContainerClassname?: string;
};

export default function DesktopNav({
  navigation,
  openNav,
  setOpenNav,
  activeBanner,
  course,
  program,
  navButtonRef,
  bannerContainerClassname = "",
}: Props) {
  const { handleLogistrationModalClick, handleLogistrationModalKeyDown } =
    useLogistrationModal();
  const { header } = navigation;
  const navMessages = useNavigationMessages();
  const { isLoggedIn } = useAuth();
  const userInfo = useMemo(() => {
    if (isLoggedIn) {
      return getUserInfo();
    }
    return null;
  }, [isLoggedIn]);
  const { lmsUserStatus, loading: lmsLoading } = useLmsUserStatus();
  const [mouseEntered, setMouseEntered] = useState(false);
  const { navSignInHref, navRegisterHref } = useAuthxNavUrls();

  //Verifying if search bar revamp test is active

  const { flagValue } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST",
    "legacy",
  );

  const isTestFlag = flagValue === "test";
  const { variant: monarchVariant } = useProductSearch();
  const isSearchBarRevampActive = isTestFlag && monarchVariant === "test";

  // Don't render auth-dependent content until both auth and LMS status are ready
  // to avoid flickering
  const isReady = isLoggedIn !== null && !lmsLoading;

  const handleMouseClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenNav(!openNav);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      setOpenNav(!openNav);
    }
  };

  const handleMouseOver = () => {
    if (!mouseEntered) {
      setOpenNav(true);
    }
    setMouseEntered(true);
  };

  const handleMouseOut = () => {
    setMouseEntered(false);
  };

  return (
    <>
      <div className="relative z-50 hidden md:flex flex-col w-full">
        <div className="relative h-[76px] flex justify-between items-center border-b border-putty-400">
          {/* Left */}
          <div className="flex items-center gap-6 flex-1 min-w-0 pl-2 sm:pl-4">
            <Link href="/" className="min-w-12 flex-shrink-0">
              <Image
                height={60}
                width={60}
                src={header.logo.url}
                alt={navMessages.logoAlt}
                title={navMessages.logoTitle}
              />
            </Link>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Button
                id="mega-nav-button"
                ref={navButtonRef}
                size="lg"
                onClick={handleMouseClick}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseOut}
                onKeyDown={handleKeyDown}
                aria-expanded={openNav}
                aria-haspopup="menu"
                aria-label={navMessages.getNavMenuAriaLabel(
                  navMessages.navButtonLabel,
                  openNav,
                )}
                className="h-10 ml-1 flex-shrink-0 focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                {navMessages.navButtonLabel}{" "}
                {openNav ? (
                  <ChevronUpIcon className="h-4 w-4 ml-2 font-bold" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 ml-2 font-bold" />
                )}
              </Button>
              <div
                className={` flex-1 min-w-0 max-w-full [&_input]:!w-full [&_input]:!min-w-0 [&_form>div]:!max-w-[min(100%,theme(width.72))]${
                  isSearchBarRevampActive
                    ? "[&_form>div:focus-within]:!max-w-[min(100%,633px)]"
                    : "[&_form>div:focus-within]:!max-w-[min(100%,504px)]"
                }`}
              >
                <ProductSearch
                  course={course}
                  program={program}
                  variant="nav-header"
                  placeholder={navMessages.searchPlaceholder}
                  embedded={false}
                  isSearchBoxColumn={false}
                  location="nav-search"
                />
              </div>
            </div>
          </div>
          {/* Right */}
          <div className="flex items-center flex-shrink-0 pr-2 sm:pr-4">
            <HeaderLanguageToggle className="mr-1 hidden lg:flex" />
            <Button variant="ghost" className="px-3" asChild>
              <a href={header.cta?.business?.url}>{navMessages.business}</a>
            </Button>
            {!isReady ? (
              // Show loading state while auth and LMS status are initializing
              <Skeleton className="h-10 w-32 mr-2" />
            ) : isLoggedIn && userInfo ? (
              <>
                <UserMenu
                  headerUrls={userInfo.headerUrls}
                  userImage={userInfo.userImageUrls?.medium}
                />
              </>
            ) : (
              <>
                <EventLink
                  href={navSignInHref}
                  text={navMessages.signIn}
                  html_id="sign-in"
                  location="nav-bar"
                  name="sign-in"
                  class_name="px-3 mr-2 whitespace-nowrap"
                  button_variant="ghost"
                  customOnClick={(e) =>
                    handleLogistrationModalClick(e, "signin")
                  }
                />
                <Button
                  variant="brand"
                  className="whitespace-nowrap"
                  onClick={(e) => handleLogistrationModalClick(e, "register")}
                  onKeyDown={(e) =>
                    handleLogistrationModalKeyDown(e, "register")
                  }
                  asChild
                >
                  <a href={navRegisterHref}>{navMessages.register}</a>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Banner */}
      {activeBanner && (
        <div
          className={`hidden lg:block absolute w-full z-40 ${bannerContainerClassname}`}
        >
          <SitewideBanner {...activeBanner} />
        </div>
      )}
    </>
  );
}
